import {
  TCortexInsightsVersion,
  TCortexOrganizationInsightsV1,
  TCortexOrganizationInsightsV2,
  TCortexRecommendationV1,
  TCortexRecommendationV2,
} from '@/types/Cortex.types';

const PRIORITY_ORDER: Record<string, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// ─── Version sniffing ───────────────────────────────────────────────────────────
// The backend stores the AI payload as an untyped blob and serves it verbatim, so a
// snapshot generated before the Story-3 contract change still arrives in the old
// free-form shape (overviewSummary / combinedScore / domainInsights / teamStrengths /
// areasOfConcern / timeline-style recommendations[]). We tag the version here and let
// the UI branch to a dedicated V1/V2 component — no data transformation, so each
// version keeps its full fidelity. When the monolith catches up the V1 branch can be
// deleted wholesale.

const looksLikeV2Insights = (value: Record<string, unknown>): boolean =>
  Array.isArray(value.skillsToFocusOn) || Array.isArray(value.coreCultureImprovements);

const looksLikeV1Insights = (value: Record<string, unknown>): boolean =>
  Array.isArray(value.recommendations) ||
  typeof value.overviewSummary === 'string' ||
  Array.isArray(value.domainInsights) ||
  Array.isArray(value.teamStrengths) ||
  Array.isArray(value.areasOfConcern);

export type TaggedCortexInsights =
  | { version: 'V1'; insights: TCortexOrganizationInsightsV1 }
  | { version: 'V2'; insights: TCortexOrganizationInsightsV2 }
  | { version: 'NONE'; insights: null };

/**
 * Sniffs the `insights` payload coming back from the cortex-insights endpoint and tags
 * it with its contract version (V1 / V2 / NONE). Does NOT transform the payload — each
 * version is rendered by its own dedicated component, so V1 stays V1 and V2 stays V2.
 */
export const tagCortexInsightsVersion = (raw: unknown): TaggedCortexInsights => {
  if (!isRecord(raw)) return { version: 'NONE', insights: null };

  if (looksLikeV2Insights(raw)) {
    return { version: 'V2', insights: raw as unknown as TCortexOrganizationInsightsV2 };
  }

  if (looksLikeV1Insights(raw)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[cortex] received a legacy (V1) insights payload — rendering via the V1 components.');
    }
    return { version: 'V1', insights: raw as unknown as TCortexOrganizationInsightsV1 };
  }

  return { version: 'NONE', insights: null };
};

// ─── Per-version recommendation list helpers ────────────────────────────────────

const byPriority = <T extends { priority?: string }>(a: T, b: T): number =>
  (PRIORITY_ORDER[(a.priority ?? '').toUpperCase()] ?? 99) -
  (PRIORITY_ORDER[(b.priority ?? '').toUpperCase()] ?? 99);

/** Orders the V2 `skillsToFocusOn` bucket by priority (Critical → Low). */
export const orderSkillsToFocusOnV2 = (
  insights?: TCortexOrganizationInsightsV2 | null,
): TCortexRecommendationV2[] => {
  if (!insights?.skillsToFocusOn) return [];
  return [...insights.skillsToFocusOn].sort(byPriority);
};

/** Orders the V2 `coreCultureImprovements` bucket by priority (Critical → Low). */
export const orderCoreCultureImprovementsV2 = (
  insights?: TCortexOrganizationInsightsV2 | null,
): TCortexRecommendationV2[] => {
  if (!insights?.coreCultureImprovements) return [];
  return [...insights.coreCultureImprovements].sort(byPriority);
};

/** Orders the V1 `recommendations[]` list by priority (Critical → Low). */
export const orderCortexRecommendationsV1 = (
  insights?: TCortexOrganizationInsightsV1 | null,
): TCortexRecommendationV1[] => {
  if (!insights?.recommendations) return [];
  return [...insights.recommendations].sort(byPriority);
};

// ─── Shared display helpers ─────────────────────────────────────────────────────

/** Priority → Chakra colour token used across the recommendation cards. */
export const cortexPriorityColor = (priority: string | undefined): string => {
  switch ((priority ?? '').toUpperCase()) {
    case 'CRITICAL':
      return 'extra.red';
    case 'HIGH':
      return 'extra.orange';
    default:
      return 'text.mediumBlue';
  }
};

/** `"TRAINING_AND_DEVELOPMENT"` → `"TRAINING & DEVELOPMENT"` for display. */
export const formatCortexCategory = (category: string | undefined): string =>
  (category ?? '').replace(/_AND_/g, ' & ').replace(/_/g, ' ').toUpperCase();

// Re-exported for convenience.
export type { TCortexInsightsVersion };
